import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useForm,
  SubmitHandler,
  // Controller,
  FormProvider,
} from "react-hook-form";
// import ReactQuill from "react-quill";

import { Button, Text } from "@pentabd/ui";
import {
  // FileRow,
  InputDateControllerRow,
  // MultiFile,
  MultiFileRow,
  SectionTitle,
  SelectControllerRow,
  SelectEmployee,
} from "@components";

import { SelectProductsTable } from ".";
import SelectedProductsTable from "./SelectedProductsTable";

import { useUser } from "@hooks/useUser";

import useActionCreator from "../../../../redux/hooks/useActionCreator";

import { RequisitionCreateType } from "@type/inventory/requisition";
import { AssetType } from "@type/inventory/asset";

import { REQ_FOR, REQ_FOR_OPTIONS } from "../constants/requisition-for";

import {
  REQUISITION_VALIDATION,
  RequisitionValidationSchemaType,
  requisitionValidationSchema,
} from "@validations/Inventory/requisition/requisition";
import { yupResolver } from "@hookform/resolvers/yup";
import { FORM_FIELDS } from "@constants/form";

import { convertMultipleDocumentData } from "@utils/documentDataConvert";

const GENERAL_INFO = FORM_FIELDS.EMPLOYEE.GENERAL_INFO;

const CreateRequisitionForm = () => {
  const [selectedAssets, setSelectedAssets] = useState<AssetType[] | []>([]);
  const [employeeID, setEmployeeID] = useState<number>();
  const [inventoryType, setInventoryType] = useState<string>("");

  const moduleName = localStorage.getItem("module");
  const module = moduleName === "ITS" ? "ICT" : "NON_ICT";

  const { t } = useTranslation();

  const method = useForm<RequisitionValidationSchemaType>({
    resolver: yupResolver(requisitionValidationSchema),
  });

  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
    watch,
    unregister,
  } = method;

  const { user } = useUser();
  const { realm_roles: roles } = user || {};
  const { createRequisition } = useActionCreator();

  const onSubmit: SubmitHandler<RequisitionValidationSchemaType> = (
    submittedData: RequisitionValidationSchemaType,
  ) => {
    const _selectedAssets = selectedAssets.map((item) => {
      return {
        nameId: item.id,
        requestAmount: item.requestAmount ? item.requestAmount : 0,
        approvedAmount: 0,
      };
    });
    const _submittedData: any = {
      ...submittedData,
      letter: "",
      type: inventoryType,
      file: submittedData.file || [],
      data: _selectedAssets,
    };
    unregister(`otherEmployee`);
    unregister(`${GENERAL_INFO.WING_DESK_IO_E0}-otherEmployee`);
    unregister(`${GENERAL_INFO.DESIGNATION}-otherEmployee`);

    createRequisition(
      employeeID as number | string,
      convertMultipleDocumentData(_submittedData as RequisitionCreateType, [
        REQUISITION_VALIDATION.FILE,
      ]),
    );
  };

  const handleProductSelection = (data: AssetType) => {
    if (!selectedAssets.some((obj: AssetType) => obj.id === data.id)) {
      setSelectedAssets((prev: AssetType[] | []) => {
        return [...prev, data];
      });
    }
  };

  const handleProductRemove = (id: number) => {
    setSelectedAssets((prev) => prev.filter((obj: AssetType) => obj.id !== id));
  };

  const handleRequestedAmount = (id: number, amount: string) => {
    setSelectedAssets((prev) =>
      prev.map((asset) => {
        if (asset.id === id) {
          return {
            ...asset,
            requestAmount: Number(amount),
          };
        } else {
          return {
            ...asset,
          };
        }
      }),
    );
  };

  const watchRequestingFor = watch(REQUISITION_VALIDATION.REQUESTER_TYPE);
  const watchOtherEmployee = watch("otherEmployee");

  useEffect(() => {
    if (watchRequestingFor !== REQ_FOR.OPTIONS.OTHERS.VALUE) {
      setEmployeeID(user?.employeeMasterId);
    } else if (
      watchRequestingFor === REQ_FOR.OPTIONS.OTHERS.VALUE &&
      watchOtherEmployee
    ) {
      setEmployeeID(watchOtherEmployee as unknown as number);
    }
  }, [watch, watchRequestingFor, watchOtherEmployee]);

  useEffect(() => {
    setInventoryType(module);
  }, [module]);

  return (
    <div className="mt-8">
      <FormProvider {...method}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <div className="d-flex flex-column gap-8 pb-8">
              <SelectControllerRow
                label="INVENTORY_REQUISITION.REQUISITION_CREATE.FORM.REQUISITION"
                control={control}
                name={REQUISITION_VALIDATION.REQUESTER_TYPE}
                register={{
                  ...register(REQUISITION_VALIDATION.REQUESTER_TYPE),
                }}
                options={REQ_FOR_OPTIONS(roles)}
                setValue={setValue}
                required
                error={errors}
              />

              {watchRequestingFor === REQ_FOR.OPTIONS.OTHERS.VALUE && (
                <div className="row">
                  <div className="col-lg-3">
                    <Text weight="semibold" color="title" size="sm">
                      {t(
                        "INVENTORY_REQUISITION.REQUISITION_CREATE.FORM.SELECT_EMPLOYEE.LABEL",
                      )}
                    </Text>
                  </div>
                  <div className="col-lg-7">
                    <SelectEmployee
                      registeredName="otherEmployee"
                      selectSingleEmployee
                    />
                  </div>
                </div>
              )}
              <InputDateControllerRow
                label="INVENTORY_REQUISITION.REQUISITION_CREATE.FORM.REQUISITION_DATE"
                control={control}
                register={{ ...register(REQUISITION_VALIDATION.REQ_DATE) }}
                errors={errors as any}
                name={REQUISITION_VALIDATION.REQ_DATE}
                required
              />
            </div>
            {selectedAssets && selectedAssets.length > 0 && (
              <SelectedProductsTable
                selectedAssets={selectedAssets}
                handleRequestedAmount={handleRequestedAmount}
                handleProductRemove={handleProductRemove}
              />
            )}

            <SelectProductsTable
              selectedAssets={selectedAssets}
              selectProduct={handleProductSelection}
            />

            <div className="border-bottom mt-10">
              <SectionTitle label="INVENTORY_REQUISITION.REQUISITION_CREATE.SUPPORTING_DOC" />
              <MultiFileRow
                name={REQUISITION_VALIDATION.FILE}
                control={control}
                setValue={setValue}
                register={register}
                fileDetails={["documentName", "date", "remarks", "memorialNo"]}
              />
            </div>

            <div className="text-end border-top pt-10">
              <Button fill="fill" type="primary" size="sm" htmlType="submit">
                {t("INVENTORY_REQUISITION.REQUISITION_CREATE.SEND_TO_ADMIN")}
              </Button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default CreateRequisitionForm;
